import { type ReactNode, useCallback, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { ParagraphFeedback } from './content-brief.interface'
import { ContentBriefFeedbackSummary } from './content-brief-feedback-summary.atom'

export interface ContentBriefProceedModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  feedbackList: ParagraphFeedback[]
  onProceed: (additionalComment: string) => void
}

const getFeedbackDescription = (count: number): string => {
  if (count === 0) {
    return 'You have no comments on this document yet.'
  }
  return `You have ${count} comment${count === 1 ? '' : 's'} on this document.`
}

export const ContentBriefProceedModal: React.FC<ContentBriefProceedModalProps> = ({
  isOpen,
  onOpenChange,
  feedbackList,
  onProceed,
}): ReactNode => {
  const [additionalComment, setAdditionalComment] = useState('')
  const feedbackCount = feedbackList.length
  const handleProceed = useCallback(() => {
    onProceed(additionalComment.trim())
    setAdditionalComment('')
    onOpenChange(false)
  }, [additionalComment, onProceed, onOpenChange])
  const handleCancel = useCallback(() => {
    setAdditionalComment('')
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="content-brief-proceed-modal sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Submit Feedback</DialogTitle>
          <DialogDescription>{getFeedbackDescription(feedbackCount)}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          {feedbackCount > 0 && <ContentBriefFeedbackSummary feedbackList={feedbackList} />}

          <div className="flex flex-col gap-2">
            <Label htmlFor="additional-comment">Additional comments (optional)</Label>
            <Textarea
              id="additional-comment"
              value={additionalComment}
              onChange={(e) => setAdditionalComment(e.target.value)}
              placeholder="Any overall feedback or additional instructions..."
              className="content-brief-additional-comment min-h-24 resize-none"
            />
          </div>
        </div>

        <DialogFooter className="[&>button]:cursor-pointer">
          <Button variant="outline" onClick={handleCancel} className="content-brief-proceed-cancel">
            Cancel
          </Button>
          <Button onClick={handleProceed} className="content-brief-proceed-submit">
            Proceed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
